import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { rapidResponseAPI, mediaAlertsAPI } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { LayoutDashboard, FileText, Bell, User, LogOut, Zap, Clock } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardPage = () => {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [mySubmissions, setMySubmissions] = useState([]);
  const [myAlerts, setMyAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [automatedStatus, setAutomatedStatus] = useState(null);
  const [automatedLoading, setAutomatedLoading] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    } else if (user) {
      loadDashboardData();
    }
  }, [user, authLoading, navigate]);

  const loadDashboardData = async () => {
    try {
      // Load user's rapid responses
      const responses = await rapidResponseAPI.getAll();
      // Filter by user email (simplified - in production, filter by user_id)
      const userResponses = responses.filter(r => r.submitter_email === user.email);
      setMySubmissions(userResponses);

      // Load user's media alerts
      const alerts = await mediaAlertsAPI.getByEmail(user.email);
      setMyAlerts(alerts);
      
      // Load automated system status
      loadAutomatedStatus();
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const loadAutomatedStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/automated-articles/status`);
      setAutomatedStatus(response.data);
    } catch (error) {
      console.error('Error loading automated status:', error);
    }
  };
  
  const startAutomatedSystem = async () => {
    setAutomatedLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/admin/automated-articles/start`);
      alert('Automated system started! It will run from 7am-1pm daily.');
      loadAutomatedStatus();
    } catch (error) {
      alert('Error starting automated system: ' + error.message);
    } finally {
      setAutomatedLoading(false);
    }
  };
  
  const stopAutomatedSystem = async () => {
    setAutomatedLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/admin/automated-articles/stop`);
      alert('Automated system stopped.');
      loadAutomatedStatus();
    } catch (error) {
      alert('Error stopping automated system: ' + error.message);
    } finally {
      setAutomatedLoading(false);
    }
  };
  
  const testAutomatedSystem = async () => {
    if (!confirm('This will generate 2 test articles. Continue?')) return;
    
    setAutomatedLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/admin/automated-articles/test?num_articles=2`);
      alert('Test completed! Check the homepage for new articles.');
    } catch (error) {
      alert('Error testing automated system: ' + error.message);
    } finally {
      setAutomatedLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (authLoading || loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-16 text-center">
        <p className="text-gray-600">Loading dashboard...</p>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-serif font-bold mb-2 flex items-center gap-3">
            <LayoutDashboard size={32} />
            Dashboard
          </h1>
          <p className="text-gray-600">Welcome back, {user.name}!</p>
        </div>
        <div className="flex gap-2">
          <Link to="/profile">
            <Button variant="outline">
              <User className="mr-2" size={16} />
              Edit Profile
            </Button>
          </Link>
          <Button onClick={handleLogout} variant="outline">
            <LogOut className="mr-2" size={16} />
            Logout
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Account</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              {user.profile_picture ? (
                <img
                  src={`${API_BASE_URL}${user.profile_picture}`}
                  alt={user.name}
                  className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                />
              ) : (
                <User size={32} className="text-gray-400" />
              )}
              <div>
                <div className="text-2xl font-bold">{user.name}</div>
                <div className="text-sm text-gray-500">{user.email}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">My Submissions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <FileText size={32} className="text-blue-500" />
              <div>
                <div className="text-2xl font-bold">{mySubmissions.length}</div>
                <div className="text-sm text-gray-500">Articles submitted</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Media Alerts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <Bell size={32} className="text-green-500" />
              <div>
                <div className="text-2xl font-bold">{myAlerts.length}</div>
                <div className="text-sm text-gray-500">Active alerts</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* My Submissions */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>My Story Submissions</CardTitle>
          <CardDescription>Articles you've submitted to The Central Coast Times</CardDescription>
        </CardHeader>
        <CardContent>
          {mySubmissions.length > 0 ? (
            <div className="space-y-4">
              {mySubmissions.map((submission) => (
                <div key={submission.id || submission._id} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-semibold mb-1">{submission.original_article_title}</h3>
                      <p className="text-sm text-gray-600 mb-2">
                        Status: <span className="font-medium capitalize">{submission.status.replace('_', ' ')}</span>
                      </p>
                      <p className="text-xs text-gray-500">
                        Submitted: {new Date(submission.submitted_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {submission.status === 'ai_draft_ready' || submission.status === 'user_editing' ? (
                        <Link to={`/review-article/${submission.id || submission._id}`}>
                          <Button size="sm">Review Draft</Button>
                        </Link>
                      ) : submission.status === 'published' && submission.article_id ? (
                        <Link to={`/article/${submission.article_id}`}>
                          <Button size="sm" variant="outline">View Article</Button>
                        </Link>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <FileText size={48} className="mx-auto mb-4 text-gray-300" />
              <p className="text-gray-600 mb-4">You haven't submitted any stories yet</p>
              <div className="flex gap-3 justify-center">
                <Link to="/submit-story">
                  <Button>Submit Counter-Story</Button>
                </Link>
                <Link to="/share-good-news">
                  <Button variant="outline" className="bg-green-50">Share Good News</Button>
                </Link>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* My Media Alerts */}
      <Card>
        <CardHeader>
          <CardTitle>My Media Alerts</CardTitle>
          <CardDescription>Get notified when your name appears in Express Advocate articles</CardDescription>
        </CardHeader>
        <CardContent>
          {myAlerts.length > 0 ? (
            <div className="space-y-4">
              {myAlerts.map((alert) => (
                <div key={alert.id || alert._id} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold mb-2">Monitoring: {alert.monitored_names.join(', ')}</h3>
                      {alert.monitored_keywords && alert.monitored_keywords.length > 0 && (
                        <p className="text-sm text-gray-600">Keywords: {alert.monitored_keywords.join(', ')}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-2">
                        Status: {alert.is_active ? '🟢 Active' : '🔴 Inactive'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Bell size={48} className="mx-auto mb-4 text-gray-300" />
              <p className="text-gray-600 mb-4">You don't have any media alerts set up</p>
              <Link to="/media-alerts">
                <Button>Create Media Alert</Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Automated Article System - Admin Only */}
      <Card className="mt-8 border-purple-200 bg-purple-50/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="text-purple-600" size={24} />
            Automated Article System
          </CardTitle>
          <CardDescription>AI-powered article generation from multiple news sources</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Status */}
            {automatedStatus && (
              <div className="bg-white rounded-lg p-4 border">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">Status</p>
                    <p className="text-lg font-semibold">
                      {automatedStatus.status.running ? (
                        <span className="text-green-600">🟢 Running</span>
                      ) : (
                        <span className="text-gray-500">⚪ Stopped</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">
                      <Clock size={14} className="inline mr-1" />
                      Schedule
                    </p>
                    <p className="text-sm">{automatedStatus.info.schedule}</p>
                    <p className="text-sm text-gray-500">{automatedStatus.info.target}</p>
                  </div>
                </div>
                
                {automatedStatus.status.last_run && (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-xs text-gray-500">
                      Last run: {new Date(automatedStatus.status.last_run).toLocaleString()}
                    </p>
                  </div>
                )}
              </div>
            )}
            
            {/* Controls */}
            <div className="flex gap-3 flex-wrap">
              <Button 
                onClick={testAutomatedSystem}
                disabled={automatedLoading}
                variant="outline"
                className="bg-blue-50 border-blue-200"
              >
                Test (Generate 2 Articles)
              </Button>
              
              {automatedStatus?.status.running ? (
                <Button 
                  onClick={stopAutomatedSystem}
                  disabled={automatedLoading}
                  variant="destructive"
                >
                  Stop System
                </Button>
              ) : (
                <Button 
                  onClick={startAutomatedSystem}
                  disabled={automatedLoading}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  Start System
                </Button>
              )}
            </div>
            
            {/* Info */}
            <div className="bg-blue-50 rounded-lg p-3 text-sm text-gray-700">
              <p className="font-medium mb-1">How it works:</p>
              <ul className="space-y-1 text-xs">
                <li>✓ Scrapes news from multiple Central Coast sources</li>
                <li>✓ AI rewrites with positive, local angle using GPT-4o</li>
                <li>✓ Finds contextually accurate images</li>
                <li>✓ Auto-categorizes and publishes 10 articles/day</li>
                <li>✓ Runs 7am-1pm daily when started</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
